import { ART_CATEGORIES, LIBRARY, artCategories, bindingsOf, summaryOf, templateDocument, templateEntries, templateEntry } from './artSamples';
import { PHOTOS } from './artDesigns/photos';
import {
  BINDINGS, TEXT_BINDINGS, blobToDataUrl, createDocument, mapImageSources, newId, normalizeDocument, prepareImageFile, validateDocument,
} from '../features/ArtEditor/headless';

// The public template generator (/studio/art-template-generator) runs without the Studio API:
// everything it needs is here, in the browser. Templates come in and go out as JSON files.

const localized = (value, locale) => value[locale] || value.pt_BR;

// The gallery ("Começar de um modelo", a carousel's new pages): the web's own catalog, the
// designs the mock serves.
export const generatorGallery = locale => ({
  categories: async ({ format }) => artCategories(locale, format),
  templates: async ({ format }) => ({ data: templateEntries().map(entry => summaryOf(entry, locale)).filter(item => item.format === format) }),
  template: async id => {
    const entry = templateEntry(id);
    if (!entry) throw Object.assign(new Error('Unknown template'), { code: 'not_found' });
    const document = templateDocument(entry, { locale });
    return { ...summaryOf(entry, locale), document, bindings: bindingsOf(document) };
  },
});

// Every category a template can be filed under, also those with no template of a format yet.
export const generatorCategories = locale => ART_CATEGORIES.map(category => ({ key: category.key, name: localized(category.name, locale) }));

// The photo library: Ginastee-hosted photos with public https URLs, the only images a catalog
// template may use.
export const photoLibrary = () => [...new Map([...Object.values(LIBRARY), ...Object.values(PHOTOS)]
  .map(source => [source.url, { id: source.url, kind: 'library', url: source.url, width: source.width, height: source.height }])).values()];

// A photo from the computer stays inside the document, as a data URL under an `asset` id: the
// Studio's "Adicionar modelo" copies it into the creator's account. Core's catalog does not
// take it (library photos only).
export async function localImage(file) {
  const prepared = await prepareImageFile(file, { maxSide: 1600 });
  return { id: newId('sas'), url: await blobToDataUrl(prepared.blob), width: prepared.width, height: prepared.height };
}

// The uploads a document carries, once each, for the gallery after a reload and the export notice.
export function documentUploads(document) {
  const found = new Map();
  if (document) mapImageSources(document, source => {
    if (source?.kind === 'asset' && source.url) found.set(source.asset_id, { id: source.asset_id, url: source.url, width: source.width, height: source.height });
    return source;
  });
  return [...found.values()];
}

// The roles ("binding": what the AI writes there) a layer can take in a template.
export const layerRoles = layer => [null, ...(layer.type === 'text' ? [...TEXT_BINDINGS, 'decoration'] : layer.type === 'image' ? ['photo', 'logo', 'decoration'] : ['decoration'])]
  .filter(role => role === null || BINDINGS.includes(role));

export const blankTemplate = (format = 'instagram::post') => ({
  document: normalizeDocument(createDocument({ format, background: { type: 'solid', color: '#ffffff' } })),
  meta: { name: '', description: null, category_key: null, tags: [] },
});

// The work in progress survives a reload (this browser only).
const DRAFT_KEY = 'ginastee:studio:template-generator:v1';
export function readGeneratorDraft(storage = window.localStorage) {
  try {
    const draft = JSON.parse(storage.getItem(DRAFT_KEY));
    if (!draft?.document || validateDocument(draft.document).length) return null;
    return { document: normalizeDocument(draft.document), meta: { ...blankTemplate().meta, ...draft.meta } };
  } catch { return null; }
}
export function saveGeneratorDraft(draft, storage = window.localStorage) { storage.setItem(DRAFT_KEY, JSON.stringify(draft)); }
